import React from 'react';
import { MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal, isEdited } from '../../utils/textSimilarity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

const AspectBadges = ({ aspects }) => {
  if (!aspects || aspects.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-2 mb-1">
      {aspects.map((aspect, idx) => (
        <span
          key={idx}
          className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700"
          title={aspect.aspectName}
        >
          {aspect.aspectName}
        </span>
      ))}
    </div>
  );
};

const BlockContent = ({ block }) => {
  // GM text content types
  if (block.type === 'gm_prose' || block.type === 'gm_text') {
    return (
      <MarkdownRenderer content={block.content} />
    );
  }

  if (block.type === 'gm_transition') {
    return (
      <div className="text-gray-600 italic">
        <MarkdownRenderer content={block.content} />
      </div>
    );
  }

  // GM media content types
  if (block.type === 'gm_image') {
    return (
      <figure className="my-4">
        {block.mediaUrl && (
          <img
            src={getMediaUrl(block.mediaUrl)}
            alt="GM image"
            className="rounded-lg max-w-full h-auto"
          />
        )}
      </figure>
    );
  }

  if (block.type === 'gm_audio') {
    return (
      <div className="my-4">
        {block.mediaUrl && (
          <audio controls className="w-full">
            <source src={getMediaUrl(block.mediaUrl)} />
            Your browser does not support audio playback.
          </audio>
        )}
      </div>
    );
  }

  // Player content - check contentType
  if (block.type === 'player_content') {
    const aspects = block.referencedAspects;
    const hasBeenEdited = block.originalContent && isEdited(block.originalContent, block.content);
    const percentOriginal = hasBeenEdited ? calculatePercentOriginal(block.originalContent, block.content) : null;

    if (block.contentType === 'prose') {
      return (
        <div>
          {hasBeenEdited && (
            <span className="inline-block text-xs px-1.5 py-0.5 mb-1 rounded bg-amber-100 text-amber-700">
              {formatPercentOriginal(percentOriginal) || 'edited'}
            </span>
          )}
          <AspectBadges aspects={aspects} />
          <MarkdownRenderer content={block.content} />
        </div>
      );
    }

    if (block.contentType === 'image') {
      return (
        <figure className="my-4">
          <AspectBadges aspects={aspects} />
          {block.mediaUrl && (
            <img
              src={getMediaUrl(block.mediaUrl)}
              alt={block.content || 'Player image'}
              className="rounded-lg max-w-full h-auto"
            />
          )}
          {block.content && (
            <figcaption className="text-sm text-gray-500 text-center mt-2">
              {block.content}
            </figcaption>
          )}
        </figure>
      );
    }

    if (block.contentType === 'audio') {
      return (
        <div className="my-4">
          <AspectBadges aspects={aspects} />
          {block.mediaUrl && (
            <audio controls className="w-full">
              <source src={getMediaUrl(block.mediaUrl)} />
              Your browser does not support audio playback.
            </audio>
          )}
          {block.content && (
            <p className="text-sm text-gray-500 text-center mt-1">
              {block.content}
            </p>
          )}
        </div>
      );
    }
  }

  // Fallback for unknown block types
  return (
    <div className="text-gray-400 italic">
      Unknown content type: {block.type} / {block.contentType}
    </div>
  );
};

export const PassagePreview = ({ blocks }) => {
  if (!blocks || blocks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No content to preview yet.</p>
      </div>
    );
  }

  return (
    <div className="prose prose-sm max-w-none">
      {blocks.map((block, index) => (
        <div key={block.id || index} className="mb-4">
          <BlockContent block={block} />
        </div>
      ))}
    </div>
  );
};
